/**
 * The tablet's local copy: IndexedDB, three object stores.
 *
 *   kv      small singletons — the reference lists, /me, the sync problems
 *   days    one DayView per work date, as last seen from the server with any
 *           queued edits applied on top (see offline.ts)
 *   outbox  the queued writes, keyed by their UUIDv7 op id, so a cursor walk
 *           is replay order
 *
 * This is a cache plus a queue, not a database. Nothing here is the truth —
 * the Worker is — and every record can be thrown away once the queue is empty.
 * It is separate from the PIN vault (credential.ts) on purpose: the vault is
 * wiped after five wrong PINs, and a wiped tablet must still be able to hand
 * over the day it entered.
 *
 * Blobs (a signature PNG, a receipt JPEG) are stored as Blobs on the queued op.
 * IndexedDB keeps them as-is; nothing is base64'd.
 */

const DB_NAME = 'quarry-store';
const DB_VERSION = 1;
const KV = 'kv';
const DAYS = 'days';
const OUTBOX = 'outbox';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      for (const name of [KV, DAYS, OUTBOX]) {
        if (!db.objectStoreNames.contains(name)) db.createObjectStore(name);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB is unavailable.'));
  });
}

function done<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function complete(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

async function withStore<T>(
  name: string,
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => Promise<T>,
): Promise<T> {
  const db = await openDb();
  try {
    const tx = db.transaction(name, mode);
    const result = await work(tx.objectStore(name));
    await complete(tx);
    return result;
  } finally {
    db.close();
  }
}

// ---------------------------------------------------------------- kv

export async function kvGet<T>(key: string): Promise<T | null> {
  return withStore(KV, 'readonly', async (store) =>
    ((await done(store.get(key))) as T | undefined) ?? null,
  );
}

export async function kvSet<T>(key: string, value: T): Promise<void> {
  await withStore(KV, 'readwrite', async (store) => { store.put(value, key); });
}

// -------------------------------------------------------------- days

export async function dayGet<T>(date: string): Promise<T | null> {
  return withStore(DAYS, 'readonly', async (store) =>
    ((await done(store.get(date))) as T | undefined) ?? null,
  );
}

export async function daySet<T>(date: string, view: T): Promise<void> {
  await withStore(DAYS, 'readwrite', async (store) => { store.put(view, date); });
}

/** The most recent date held, for synthesising a day that was never loaded. */
export async function dayLatest<T>(): Promise<{ date: string; view: T } | null> {
  return withStore(DAYS, 'readonly', async (store) => {
    const keys = (await done(store.getAllKeys())) as string[];
    if (keys.length === 0) return null;
    const date = [...keys].sort().at(-1)!;
    const view = (await done(store.get(date))) as T;
    return { date, view };
  });
}

// ------------------------------------------------------------ outbox

export async function outboxPut<T>(id: string, record: T): Promise<void> {
  await withStore(OUTBOX, 'readwrite', async (store) => { store.put(record, id); });
}

export async function outboxDelete(id: string): Promise<void> {
  await withStore(OUTBOX, 'readwrite', async (store) => { store.delete(id); });
}

/** Every queued op, oldest first — UUIDv7 keys sort by the time they were minted. */
export async function outboxAll<T>(): Promise<{ id: string; record: T }[]> {
  return withStore(OUTBOX, 'readonly', async (store) => {
    const keys = (await done(store.getAllKeys())) as string[];
    const values = (await done(store.getAll())) as T[];
    return keys
      .map((id, index) => ({ id, record: values[index]! }))
      .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  });
}

export async function outboxCount(): Promise<number> {
  return withStore(OUTBOX, 'readonly', async (store) => done(store.count()));
}
